<script setup>
import { Button } from '@dashhudson/dashing-ui/button';
import { Menu } from '@dashhudson/dashing-ui/menu';
import { DataViewToolbar } from '@dashhudson/dashing-ui/data-view-toolbar';
import { ChannelFilterTrigger } from '@dashhudson/dashing-ui/channel-filter-button';
import { useConfirmationDialog } from '@dashhudson/dashing-ui/confirmation-dialog';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { computed } from 'vue';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import {
  MANAGE_USAGE_CLICK_LOCATION_GDI,
  SOCIAL_LISTENING_DRAWERS,
  SOCIAL_LISTENING_ROUTE_NAMES,
  SOCIAL_LISTENING_TOASTS,
  TOPIC_TEMPLATE_FLOWS,
} from '@/app/socialListening/constants';
import { comparisonDeletedEvent } from '@/app/socialListening/utils/mixpanel-events';
import { useRouter } from 'vue-router';
import {
  CHANNEL_FILTER_OPTIONS,
  defaultFilterValues,
  TOPIC_DATE_FILTER_OPTIONS,
} from '@/app/socialListening/constants/listeningFilters';
import DashingPageHeader from '@/components/dashingUI/DashingPageHeader.vue';
import ReportingPeriodFilterTrigger from '@/components/dashingUI/ReportingPeriodFilterTrigger.vue';
import mapValues from 'lodash/mapValues';

const comparisonStore = useSocialListeningComparisonsStore();
const router = useRouter();
const { launchDrawer } = useDrawer();
const { addToast } = useToast({ life: 5000 });

const selectedComparison = computed(() => comparisonStore.selectedComparison);
const title = computed(() => selectedComparison.value?.name ?? '');

function manageLimits() {
  launchDrawer({
    name: SOCIAL_LISTENING_DRAWERS.MANAGE_TOPIC_LIMITS,
    props: { from: MANAGE_USAGE_CLICK_LOCATION_GDI.COMPARISON },
  });
}

function createComparison() {
  comparisonStore.clearDraftComparison();
  launchDrawer({
    name: SOCIAL_LISTENING_DRAWERS.TOPIC_TEMPLATE_CREATOR,
    props: { flow: TOPIC_TEMPLATE_FLOWS.MULTIPLE_TOPIC },
  });
}

function editComparison() {
  comparisonStore.clearDraftComparison();
  launchDrawer({
    name: SOCIAL_LISTENING_DRAWERS.TOPIC_TEMPLATE_CREATOR,
    props: { flow: TOPIC_TEMPLATE_FLOWS.MULTIPLE_TOPIC, comparisonId: selectedComparison.value.id },
  });
}

async function deleteComparison() {
  const comparisonId = selectedComparison.value.id;
  const comparisonName = selectedComparison.value.name;

  const result = await comparisonStore.deleteComparison(comparisonId);
  if (result?.status !== 204) {
    addToast({ ...SOCIAL_LISTENING_TOASTS.DELETE_COMPARISON.ERROR });
  }
  if (result?.status === 204) {
    comparisonDeletedEvent({
      comparisonId,
      comparisonName,
    });
    await router.push({ name: SOCIAL_LISTENING_ROUTE_NAMES.OVERVIEW });
  }
}

const { showConfirmationDialog } = useConfirmationDialog({
  header: 'Delete Comparison',
  acceptLabel: 'Delete Comparison',
  group: 'global',
  onAccept: async () => {
    await deleteComparison();
  },
  acceptSeverity: 'danger',
});

function confirmDeleteComparison() {
  showConfirmationDialog({
    message: `Are you sure you want to permanently delete the ${selectedComparison.value?.name} Comparison?`,
  });
}

const contextMenuItems = computed(() => [
  {
    label: 'Create Comparison',
    command: () => createComparison(),
    icon: 'plus',
  },
  {
    label: 'Manage Limits',
    command: () => manageLimits(),
    icon: 'settings',
  },
  {
    label: 'Delete Comparison',
    command: confirmDeleteComparison,
    class: 'dash-text-danger dash-fill-icon-danger',
    icon: 'delete',
  },
]);
const defaultChannels = defaultFilterValues(CHANNEL_FILTER_OPTIONS);

const customReportingPeriodPresetMap = {
  ...mapValues(TOPIC_DATE_FILTER_OPTIONS, (o) => o.label),
  CUSTOM: 'Custom',
  ALL_TIME: 'All Time',
};
</script>

<template>
  <DashingPageHeader :title="title">
    <template #actions>
      <Button size="lg" severity="primary" icon-right="edit" @click="editComparison"
        >Edit Comparison</Button
      >
      <Menu :model="contextMenuItems" :dropdown="{ placement: 'bottom-end' }">
        <Button size="lg" icon="dots-vertical" severity="primary" />
      </Menu>
    </template>
    <DataViewToolbar hide-view-controls hide-select-controls>
      <template #filterToolbar>
        <ChannelFilterTrigger
          :icon-right="null"
          filter="sources"
          :default-channels="defaultChannels"
        />
        <ReportingPeriodFilterTrigger
          filter="sourceCreated"
          :preset-map="customReportingPeriodPresetMap"
        />
      </template>
    </DataViewToolbar>
  </DashingPageHeader>
</template>
